import type { WeatherDetail } from '@/types/telemetry';
import { weatherConditionInfo } from '@/lib/weather';
import { useI18n } from '@/context/I18nContext';

interface WeatherCardProps {
  weather: {
    condition: string | number;
    temperatureC?: number | null;
    feelsLikeC?: number | null;
    windSpeedKmh?: number | null;
    windDirection?: string | null;
    humidityPct?: number | null;
    cloudCoverPct?: number | null;
    precipitationMm?: number | null;
    pressureHpa?: number | null;
  };
}

export function WeatherCard({ weather }: WeatherCardProps) {
  const { t } = useI18n();
  const cond = weatherConditionInfo(weather.condition, t);
  const temp = weather.temperatureC != null ? weather.temperatureC.toFixed(1) + '°C' : '—';
  const feelsLike = weather.feelsLikeC != null ? weather.feelsLikeC.toFixed(1) + '°C' : '—';
  const wind = weather.windSpeedKmh != null ? weather.windSpeedKmh.toFixed(0) + ' km/h' : '—';
  const windDir = weather.windDirection || '';
  const humidity = weather.humidityPct != null ? weather.humidityPct + '%' : '—';
  const cloud = weather.cloudCoverPct != null ? weather.cloudCoverPct + '%' : '—';
  const precip = weather.precipitationMm != null ? weather.precipitationMm.toFixed(1) + ' mm' : '—';
  const pressure = weather.pressureHpa != null ? weather.pressureHpa.toFixed(0) + ' hPa' : '—';

  const details = [
    { icon: '💨', value: `${wind} ${windDir}`, label: t('weather.wind') },
    { icon: '💧', value: humidity, label: t('weather.humidity') },
    { icon: '☁️', value: cloud, label: t('weather.cloudCover') },
    { icon: '🌧️', value: precip, label: t('weather.precipitation') },
    { icon: '🔽', value: pressure, label: t('weather.pressure') },
  ];

  return (
    <div className="glass-card p-4 sm:p-6 overflow-hidden">
      <div className="flex items-center gap-4 pb-4 border-b border-border-default mb-4">
        <span className="text-4xl shrink-0">{cond.icon}</span>
        <div className="flex flex-col">
          <span className="text-2xl font-bold mono text-text-primary">{temp}</span>
          <span className="text-xs text-text-muted">{t('weather.feels')} {feelsLike}</span>
        </div>
        <span className="ml-auto text-sm font-medium text-text-secondary">{cond.label}</span>
      </div>
      <div className="flex flex-wrap gap-4 sm:gap-6">
        {details.map(d => (
          <div key={d.label} className="flex flex-col items-center min-w-[80px]">
            <span className="text-lg mb-0.5">{d.icon}</span>
            <span className="text-sm font-semibold mono text-text-primary">{d.value}</span>
            <span className="text-[0.66rem] text-text-muted uppercase tracking-wider">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
